/**
 * ShortLinkItem - Individual short link display with actions
 */

import React from 'react';
import { Text, HStack, IconButton, useClipboard, Badge } from '@chakra-ui/react';
import { Copy, ExternalLink } from 'lucide-react';
import { ConfirmDeleteButton } from '../../../../common/ConfirmDeleteButton';

export const ShortLinkItem: React.FC<{
    url: string;
    code: string;
    onDelete: (code: string) => Promise<void>;
    shareConfig?: any;
}> = ({ url, code, onDelete, shareConfig }) => {
    const { onCopy, hasCopied } = useClipboard(url);

    // Get hidden fields from share_config
    const hiddenFields: string[] = [];
    if (shareConfig?.fields) {
        const fieldLabels: Record<string, string> = {
            name: 'Name',
            birth_time: 'Time',
            birth_place: 'Place',
            coordinates: 'Location',
            timezone: 'TZ',
        };

        Object.entries(shareConfig.fields).forEach(([key, value]) => {
            if (value === false) {
                hiddenFields.push(fieldLabels[key] || key);
            }
        });
    }

    return (
        <HStack
            spacing={2}
            px={2}
            py={1}
            bg="gray.50"
            borderRadius="md"
            _hover={{ bg: 'gray.100' }}
        >
            <HStack spacing={0} flexShrink={0}>
                <IconButton
                    aria-label="Copy link"
                    icon={<Copy size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={onCopy}
                    colorScheme={hasCopied ? 'green' : 'gray'}
                />
                <IconButton
                    aria-label="Open link"
                    icon={<ExternalLink size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => window.open(url, '_blank')}
                />
                <ConfirmDeleteButton
                    onDelete={() => onDelete(code)}
                />
            </HStack>

            {hiddenFields.length > 0 && (
                <HStack spacing={1} flexShrink={0}>
                    {hiddenFields.map((field) => (
                        <Badge
                            key={field}
                            fontSize="2xs"
                            colorScheme="orange"
                            variant="subtle"
                            px={1}
                        >
                            {field}
                        </Badge>
                    ))}
                </HStack>
            )}

            <Text
                fontSize="xs"
                fontFamily="var(--font-ui-mono)"
                color="blue.600"
                flex={1}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
            >
                {url}
            </Text>
        </HStack>
    );
};
